import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Loader2, Search, Bookmark, BookmarkCheck, ExternalLink, Calendar, 
  PoundSterling, Building2, Target, Clock, Sparkles, ChevronRight,
  Filter, X, AlertCircle, CheckCircle, TrendingUp
} from "lucide-react";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Match label badge colors
const MATCH_COLORS = {
  "High match": "bg-green-100 text-green-700 border-green-200",
  "Good match": "bg-blue-100 text-blue-700 border-blue-200",
  "Potential match": "bg-amber-100 text-amber-700 border-amber-200"
};

// Status badge colors and labels
const STATUS_CONFIG = {
  "open": { color: "bg-green-100 text-green-700", label: "Open" },
  "opening_soon": { color: "bg-amber-100 text-amber-700", label: "Opening Soon" },
  "upcoming": { color: "bg-blue-100 text-blue-700", label: "Upcoming" },
  "closed": { color: "bg-slate-100 text-slate-500", label: "Closed" }
};

// Format date to readable string
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Calculate days until date
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  return diff;
};

// Opportunity card component
function OpportunityCard({ opportunity, onSave, onUnsave, isSaved, onViewDetails }) {
  const deadlineDays = opportunity.deadline ? daysUntil(opportunity.deadline) : null;
  const opensDays = opportunity.opens_at ? daysUntil(opportunity.opens_at) : null;
  const isUrgent = deadlineDays !== null && deadlineDays <= 14 && deadlineDays > 0;
  const isPassed = deadlineDays !== null && deadlineDays <= 0;
  
  // Determine actual status based on dates
  let displayStatus = opportunity.status;
  if (opportunity.status === "open" && isPassed) {
    displayStatus = "closed";
  } else if (opportunity.opens_at && opensDays > 0) {
    displayStatus = "upcoming";
  }
  
  const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG["open"];
  
  const formatFunding = () => {
    if (!opportunity.funding_min && !opportunity.funding_max) return "Varies";
    if (opportunity.funding_min && opportunity.funding_max) {
      const min = parseInt(opportunity.funding_min);
      const max = parseInt(opportunity.funding_max);
      if (max >= 1000000) {
        return `£${(min / 1000).toFixed(0)}K - £${(max / 1000000).toFixed(1)}M`;
      }
      return `£${(min / 1000).toFixed(0)}K - £${(max / 1000).toFixed(0)}K`;
    }
    if (opportunity.funding_max) {
      const max = parseInt(opportunity.funding_max);
      return max >= 1000000 ? `Up to £${(max / 1000000).toFixed(1)}M` : `Up to £${(max / 1000).toFixed(0)}K`;
    }
    return `From £${(parseInt(opportunity.funding_min) / 1000).toFixed(0)}K`;
  };

  return (
    <Card className={`border transition-all hover:shadow-md ${
      displayStatus === "closed" ? "opacity-60" : ""
    } ${opportunity.match_label === "High match" ? "border-green-200" : "border-slate-200"}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={MATCH_COLORS[opportunity.match_label] || "bg-slate-100 text-slate-600"}>
                {opportunity.match_label}
              </Badge>
              <Badge className={statusConfig.color}>
                {statusConfig.label}
              </Badge>
            </div>
            <h3 className="font-semibold text-lg text-slate-900">{opportunity.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" />
              {opportunity.organisation}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); isSaved ? onUnsave(opportunity.opportunity_id) : onSave(opportunity.opportunity_id); }}
            className={isSaved ? "text-amber-500" : "text-slate-400 hover:text-amber-500"}
          >
            {isSaved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
          </Button>
        </div>

        {/* Status-specific info */}
        <div className="flex flex-wrap gap-4 mb-4">
          {displayStatus === "open" && opportunity.deadline && (
            <div className={`flex items-center gap-1.5 text-sm ${isUrgent ? "text-red-600 font-medium" : "text-slate-600"}`}>
              <Clock className="w-4 h-4" />
              {deadlineDays !== null && deadlineDays > 0 ? (
                <>
                  <span className="font-medium">{deadlineDays} days left</span>
                  <span className="text-slate-400">• {formatDate(opportunity.deadline)}</span>
                </>
              ) : (
                "Rolling deadline"
              )}
            </div>
          )}
          
          {displayStatus === "upcoming" && opportunity.opens_at && (
            <div className="flex items-center gap-1.5 text-sm text-blue-600">
              <Calendar className="w-4 h-4" />
              <span>Opens {formatDate(opportunity.opens_at)}</span>
              {opensDays > 0 && <span className="text-slate-400">({opensDays} days)</span>}
            </div>
          )}
          
          {displayStatus === "closed" && (
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Closed</span>
              {opportunity.next_round && (
                <span className="text-blue-600 ml-2">Next round: {formatDate(opportunity.next_round)}</span>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <PoundSterling className="w-4 h-4" />
            {formatFunding()}
          </div>
          <Badge variant="outline" className="text-xs">
            {opportunity.funding_type.charAt(0).toUpperCase() + opportunity.funding_type.slice(1)}
          </Badge>
        </div>

        {/* Why it matches */}
        {opportunity.why_matches && (
          <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg mb-4">
            <p className="text-sm text-teal-800">
              <span className="font-medium">Why this matches you: </span>
              {opportunity.why_matches}
            </p>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {opportunity.therapeutic_areas?.slice(0, 3).map((area, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">{area}</Badge>
          ))}
          {opportunity.modality_tags?.slice(0, 2).map((mod, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">{mod}</Badge>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onViewDetails(opportunity)}>
            View Details
          </Button>
          {opportunity.application_link && (
            <Button className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2" onClick={() => window.open(opportunity.application_link, '_blank')}>
              Apply <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Opportunity detail dialog
function OpportunityDetailDialog({ opportunity, open, onClose, onSave, onUnsave, isSaved }) {
  if (!opportunity) return null;
  
  const formatFunding = () => {
    if (!opportunity.funding_min && !opportunity.funding_max) return "Varies / negotiable";
    if (opportunity.funding_min && opportunity.funding_max) {
      return `£${opportunity.funding_min.toLocaleString()} - £${opportunity.funding_max.toLocaleString()}`;
    }
    if (opportunity.funding_max) return `Up to £${opportunity.funding_max.toLocaleString()}`;
    return `From £${opportunity.funding_min.toLocaleString()}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={MATCH_COLORS[opportunity.match_label] || "bg-slate-100"}>{opportunity.match_label || "Match"}</Badge>
            <Badge className={STATUS_CONFIG[opportunity.status]?.color || "bg-slate-100"}>
              {STATUS_CONFIG[opportunity.status]?.label || opportunity.status}
            </Badge>
          </div>
          <DialogTitle className="text-xl">{opportunity.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-base">
            <Building2 className="w-4 h-4" />
            {opportunity.organisation}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Key info grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500 mb-1">Funding Range</div>
              <div className="font-semibold text-slate-900">{formatFunding()}</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500 mb-1">Type</div>
              <div className="font-semibold text-slate-900 capitalize">{opportunity.funding_type}</div>
            </div>
            {opportunity.status === "open" && opportunity.deadline && (
              <div className={`p-3 rounded-lg ${daysUntil(opportunity.deadline) <= 14 ? "bg-red-50" : "bg-slate-50"}`}>
                <div className={`text-sm mb-1 ${daysUntil(opportunity.deadline) <= 14 ? "text-red-500" : "text-slate-500"}`}>
                  Deadline
                </div>
                <div className={`font-semibold ${daysUntil(opportunity.deadline) <= 14 ? "text-red-700" : "text-slate-900"}`}>
                  {formatDate(opportunity.deadline)}
                  {daysUntil(opportunity.deadline) > 0 && (
                    <span className="text-sm font-normal ml-2">({daysUntil(opportunity.deadline)} days)</span>
                  )}
                </div>
              </div>
            )}
            {opportunity.status === "upcoming" && opportunity.opens_at && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-600 mb-1">Opens</div>
                <div className="font-semibold text-blue-700">
                  {formatDate(opportunity.opens_at)}
                  {daysUntil(opportunity.opens_at) > 0 && (
                    <span className="text-sm font-normal ml-2">({daysUntil(opportunity.opens_at)} days)</span>
                  )}
                </div>
              </div>
            )}
            {opportunity.status === "closed" && opportunity.next_round && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <div className="text-sm text-amber-600 mb-1">Next Round</div>
                <div className="font-semibold text-amber-700">{formatDate(opportunity.next_round)}</div>
              </div>
            )}
          </div>

          {/* Why it matches */}
          {opportunity.why_matches && (
            <div className="p-4 bg-teal-50 border border-teal-100 rounded-lg">
              <h4 className="font-medium text-teal-900 mb-1 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Why this matches you
              </h4>
              <p className="text-teal-800">{opportunity.why_matches}</p>
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="font-medium text-slate-900 mb-2">Description</h4>
            <p className="text-slate-600">{opportunity.description}</p>
          </div>

          {/* Eligibility */}
          <div>
            <h4 className="font-medium text-slate-900 mb-2">Eligibility</h4>
            <div className="space-y-2">
              {opportunity.stage_eligibility?.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-sm text-slate-500 min-w-[100px]">Stage:</span>
                  <div className="flex flex-wrap gap-1">
                    {opportunity.stage_eligibility.map((stage, idx) => (
                      <Badge key={idx} variant="outline">{stage}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {opportunity.geography_eligibility?.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-sm text-slate-500 min-w-[100px]">Geography:</span>
                  <span className="text-sm text-slate-700">{opportunity.geography_eligibility.join(", ")}</span>
                </div>
              )}
              {opportunity.therapeutic_areas?.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-sm text-slate-500 min-w-[100px]">Focus areas:</span>
                  <div className="flex flex-wrap gap-1">
                    {opportunity.therapeutic_areas.map((area, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{area}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {opportunity.modality_tags?.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-sm text-slate-500 min-w-[100px]">Modalities:</span>
                  <div className="flex flex-wrap gap-1">
                    {opportunity.modality_tags.map((mod, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{mod}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => isSaved ? onUnsave(opportunity.opportunity_id) : onSave(opportunity.opportunity_id)}
          >
            {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {isSaved ? "Saved" : "Save"}
          </Button>
          {opportunity.application_link && (
            <Button className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2" onClick={() => window.open(opportunity.application_link, '_blank')}>
              Apply Now <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FindGrants() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState([]);
  const [openingSoon, setOpeningSoon] = useState([]);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("recommended");
  
  // Filters for explore view
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
    fetchSaved();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/funding-opportunities/matched`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecommended(data.recommended || []);
        setOpeningSoon(data.opening_soon || []);
        setAllOpportunities(data.all_matched || []);
      }
    } catch (error) {
      console.error("Failed to fetch opportunities:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaved = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/funding-opportunities/saved`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSavedIds(data.opportunities?.map(o => o.opportunity_id) || []);
      }
    } catch (error) {
      console.error("Failed to fetch saved:", error);
    }
  };

  const handleSave = async (oppId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/funding-opportunities/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ opportunity_id: oppId })
      });
      if (response.ok) {
        setSavedIds([...savedIds, oppId]);
        toast.success("Opportunity saved");
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleUnsave = async (oppId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/funding-opportunities/save/${oppId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setSavedIds(savedIds.filter(id => id !== oppId));
        toast.success("Removed from saved");
      }
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleViewDetails = (opp) => {
    setSelectedOpp(opp);
    setShowDetail(true);
  };

  // Filter opportunities for explore view - memoized to avoid recalculation on every render
  const filteredOpportunities = useMemo(() => allOpportunities.filter(opp => {
    if (search && !opp.name.toLowerCase().includes(search.toLowerCase()) && 
        !opp.organisation.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    if (typeFilter !== "all" && opp.funding_type !== typeFilter) return false;
    if (statusFilter !== "all" && opp.status !== statusFilter) return false;
    return true;
  }), [allOpportunities, search, typeFilter, statusFilter]);
  
  // Memoize saved opportunities to avoid re-filtering on every render
  const savedOpportunities = useMemo(() => 
    allOpportunities.filter(opp => savedIds.includes(opp.opportunity_id)),
    [allOpportunities, savedIds]
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="page-title">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Grants & Funding
          </h1>
          <p className="text-slate-600 mt-2">
            Personalised funding opportunities based on your company profile
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="recommended" className="gap-2" data-testid="tab-recommended">
              <Target className="w-4 h-4" />
              Recommended
            </TabsTrigger>
            <TabsTrigger value="saved" className="gap-2" data-testid="tab-saved">
              <Bookmark className="w-4 h-4" />
              Saved ({savedIds.length})
            </TabsTrigger>
            <TabsTrigger value="explore" className="gap-2" data-testid="tab-explore">
              <Search className="w-4 h-4" />
              Explore All
            </TabsTrigger>
          </TabsList>

          {/* Recommended Tab */}
          <TabsContent value="recommended" className="space-y-8">
            {/* Top Recommendations */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Recommended for You
                </h2>
                <Badge variant="outline">{recommended.length} opportunities</Badge>
              </div>
              
              {recommended.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-900 mb-2">No matches yet</h3>
                    <p className="text-slate-500 mb-4">Complete your company profile to get personalised recommendations</p>
                    <Button onClick={() => navigate("/profile")} className="gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Complete Profile
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommended.map((opp) => (
                    <OpportunityCard
                      key={opp.opportunity_id}
                      opportunity={opp}
                      onSave={handleSave}
                      onUnsave={handleUnsave}
                      isSaved={savedIds.includes(opp.opportunity_id)}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Opening Soon */}
            {openingSoon.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Opening Soon (Next 90 Days)
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {openingSoon.map((opp) => (
                    <OpportunityCard
                      key={opp.opportunity_id}
                      opportunity={opp}
                      onSave={handleSave}
                      onUnsave={handleUnsave}
                      isSaved={savedIds.includes(opp.opportunity_id)}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* CTA to explore more */}
            <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50">
              <CardContent className="p-6 text-center">
                <TrendingUp className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h3 className="font-medium text-slate-900 mb-2">Want to see more opportunities?</h3>
                <p className="text-slate-500 mb-4">Browse all {allOpportunities.length} matched opportunities</p>
                <Button variant="outline" onClick={() => setActiveTab("explore")} className="gap-2">
                  Explore All <ChevronRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Tab */}
          <TabsContent value="saved">
            {savedIds.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Bookmark className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-900 mb-2">No saved opportunities</h3>
                  <p className="text-slate-500">Click the bookmark icon on any opportunity to save it here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedOpportunities.map((opp) => (
                    <OpportunityCard
                      key={opp.opportunity_id}
                      opportunity={opp}
                      onSave={handleSave}
                      onUnsave={handleUnsave}
                      isSaved={true}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
              </div>
            )}
          </TabsContent>

          {/* Explore Tab */}
          <TabsContent value="explore">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search opportunities..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="grant">Grants</SelectItem>
                      <SelectItem value="programme">Programmes</SelectItem>
                      <SelectItem value="partnership">Partnerships</SelectItem>
                      <SelectItem value="accelerator">Accelerators</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-3">
              {filteredOpportunities.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-900 mb-2">No opportunities found</h3>
                    <p className="text-slate-500">Try adjusting your filters</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">Opportunity</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Funding</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Deadline / Opens</TableHead>
                        <TableHead>Match</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOpportunities.map((opp) => {
                        const deadlineDays = opp.deadline ? daysUntil(opp.deadline) : null;
                        const opensDays = opp.opens_at ? daysUntil(opp.opens_at) : null;
                        const isPassed = deadlineDays !== null && deadlineDays <= 0;
                        let displayStatus = opp.status;
                        if (opp.status === "open" && isPassed) displayStatus = "closed";
                        else if (opp.opens_at && opensDays > 0) displayStatus = "upcoming";
                        const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG["open"];
                        
                        return (
                        <TableRow key={opp.opportunity_id} className={`cursor-pointer hover:bg-slate-50 ${displayStatus === "closed" ? "opacity-60" : ""}`} onClick={() => handleViewDetails(opp)}>
                          <TableCell>
                            <div>
                              <div className="font-medium text-slate-900">{opp.name}</div>
                              <div className="text-sm text-slate-500">{opp.organisation}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{opp.funding_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {opp.funding_max ? `Up to £${(parseInt(opp.funding_max) / 1000000).toFixed(1)}M` : "Varies"}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          </TableCell>
                          <TableCell>
                            {displayStatus === "open" && opp.deadline ? (
                              <div className={deadlineDays <= 14 ? "text-red-600 font-medium" : "text-slate-600"}>
                                <div>{formatDate(opp.deadline)}</div>
                                <div className="text-xs">{deadlineDays}d left</div>
                              </div>
                            ) : displayStatus === "upcoming" && opp.opens_at ? (
                              <div className="text-blue-600">
                                <div>{formatDate(opp.opens_at)}</div>
                                <div className="text-xs">Opens in {opensDays}d</div>
                              </div>
                            ) : displayStatus === "closed" && opp.next_round ? (
                              <div className="text-amber-600">
                                <div>Next: {formatDate(opp.next_round)}</div>
                              </div>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={MATCH_COLORS[opp.match_label]}>{opp.match_label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); savedIds.includes(opp.opportunity_id) ? handleUnsave(opp.opportunity_id) : handleSave(opp.opportunity_id); }}
                                className={savedIds.includes(opp.opportunity_id) ? "text-amber-500" : "text-slate-400"}
                              >
                                {savedIds.includes(opp.opportunity_id) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                              </Button>
                              {opp.application_link && displayStatus === "open" && (
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); window.open(opp.application_link, '_blank'); }}>
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )})}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <OpportunityDetailDialog
          opportunity={selectedOpp}
          open={showDetail}
          onClose={() => setShowDetail(false)}
          onSave={handleSave}
          onUnsave={handleUnsave}
          isSaved={selectedOpp ? savedIds.includes(selectedOpp.opportunity_id) : false}
        />
      </div>
    </AppLayout>
  );
}
